import { call, takeLatest, put, takeEvery } from 'redux-saga/effects';
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import EcommerceOrderServices from 'shared/services/EcommerceOrderService';
import {
  getEcommerceOrderData,
  setEcommerceOrderData,
  actionFail,
  buildEcommerceOrderPage,
  setIndividualEcommerceOrderInfo,
  createEcommerceOrder,
  createEcommerceOrderSuccess,
  updateEcommerceOrder,
  updateEcommerceOrderSuccess,
  cancelEcommerceOrder,
  cancelEcommerceOrderSuccess,
  refundEcommerceOrder,
  refundEcommerceOrderSuccess,
  validateCoupon,
  setCouponValidationResult,
  setCouponValidationError,
  generateShareLink,
  setShareLinkSuccess,
  getAvailablePaymentMethods,
  setAvailablePaymentMethods,
  attachPaymentMethod
} from './ecommerceOrder.redux';
import { TAction, TPaginationRequest } from 'shared/types';

import { openNotificationMessage } from '../../shared/utils/NotificationMessages';
import { NotificationMessages } from 'shared/enums';
import { IEcommerceOrder } from 'shared/interfaces/ecommerce-order.interface';

function* getEcommerceOrder({ payload }: TAction<TPaginationRequest>) {
  try {
    const result: AxiosResponse<IEcommerceOrder[]> = yield call(
      EcommerceOrderServices.getEcommerceOrderInfo,
      payload
    );
    yield put(setEcommerceOrderData(result.data));
  } catch (error) {
    console.log('error', error);
  }
}

function* buildIndividualPage({ payload }: TAction<any>) {
  try {
    const result: AxiosResponse<IEcommerceOrder[]> = yield call(
      EcommerceOrderServices.getOneEcommerceOrder,
      payload.id
    );
    yield put(setIndividualEcommerceOrderInfo(result));
  } catch (error: any) {
    console.log('error', error);
    yield put(actionFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Something went wrong while building page.'
    });
  }
}

function* createEcommerceOrderSaga({ payload }: TAction<any>) {
  try {
    yield call(EcommerceOrderServices.createEcommerceOrder, payload.orderData);
    yield put(createEcommerceOrderSuccess());
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Ecommerce order was successfully created.'
    });
    if (payload.onSuccess) {
      payload.onSuccess();
    }
  } catch (error: any) {
    console.log('error', error);
    yield put(actionFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Ecommerce order creation was unsuccessful.'
    });
  }
}

function* updateEcommerceOrderSaga({ payload }: TAction<any>) {
  try {
    yield call(
      EcommerceOrderServices.updateEcommerceOrder,
      payload.id,
      payload.orderData
    );
    yield put(updateEcommerceOrderSuccess());
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Ecommerce order was successfully updated.'
    });
    if (payload.onSuccess) {
      payload.onSuccess();
    }
  } catch (error: any) {
    console.log('error', error);
    yield put(actionFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Ecommerce order update was unsuccessful.'
    });
  }
}

function* cancelEcommerceOrderSaga({ payload }: TAction<any>) {
  try {
    yield call(EcommerceOrderServices.cancelEcommerceOrder, payload.id);
    yield put(cancelEcommerceOrderSuccess());
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Ecommerce order was successfully cancelled.'
    });
    if (payload.onSuccess) {
      payload.onSuccess();
    }
  } catch (error: any) {
    console.log('error', error);
    yield put(actionFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Ecommerce order cancellation was unsuccessful.'
    });
  }
}

function* refundEcommerceOrderSaga({ payload }: TAction<any>) {
  try {
    yield call(
      EcommerceOrderServices.refundEcommerceOrder,
      payload.id,
      payload.refundData
    );
    yield put(refundEcommerceOrderSuccess());
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Ecommerce order refund was successfully processed.'
    });
    if (payload.onSuccess) {
      payload.onSuccess();
    }
  } catch (error: any) {
    console.log('error', error);
    yield put(actionFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Ecommerce order refund was unsuccessful.'
    });
  }
}

function* validateCouponSaga({ payload }: TAction<any>) {
  try {
    const result: AxiosResponse<any> = yield call(
      EcommerceOrderServices.validateCoupon,
      payload.couponData
    );
    yield put(setCouponValidationResult(result));
  } catch (error: any) {
    console.log('error', error);
    const errorMessage = error?.response?.data?.error || 'Invalid coupon code';
    yield put(setCouponValidationError(errorMessage));
  }
}

function* generateShareLinkSaga({ payload }: TAction<any>): any {
  try {
    const result: any = yield call(
      EcommerceOrderServices.generateShareLink,
      payload.recordId
    );
    yield put(
      setShareLinkSuccess({
        recordId: payload.recordId,
        url: result.shareUrl
      })
    );
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Failed to generate share link. Please try again.'
    });
  }
}

function* getAvailablePaymentMethodsSaga({
  payload
}: TAction<{ patientId: string }>): any {
  try {
    const result: any = yield call(
      EcommerceOrderServices.getAvailablePaymentMethods,
      payload.patientId
    );
    yield put(setAvailablePaymentMethods(result));
  } catch (error: any) {
    console.log('error', error);
    yield put(setAvailablePaymentMethods([]));
  }
}

function* attachPaymentMethodSaga({
  payload
}: TAction<{
  patientId: string;
  paymentMethodId: string;
  onSuccess?: (paymentMethodId: string) => void;
  onError?: () => void;
}>): any {
  try {
    yield call(EcommerceOrderServices.attachPaymentMethod, {
      patientId: payload.patientId,
      paymentMethodId: payload.paymentMethodId
    });
    // Refresh payment methods list after successful attachment
    const result: any = yield call(
      EcommerceOrderServices.getAvailablePaymentMethods,
      payload.patientId
    );
    yield put(setAvailablePaymentMethods(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Payment method added successfully.'
    });
    if (payload.onSuccess) {
      payload.onSuccess(payload.paymentMethodId);
    }
  } catch (error: any) {
    console.log('error', error);
    yield put(setAvailablePaymentMethods([]));
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Failed to add payment method. Please try again.'
    });
    if (payload.onError) {
      payload.onError();
    }
  }
}

function* EcommerceOrders() {
  yield takeLatest(getEcommerceOrderData.type, getEcommerceOrder);
  yield takeLatest(buildEcommerceOrderPage.type, buildIndividualPage);
  yield takeEvery(createEcommerceOrder.type, createEcommerceOrderSaga);
  yield takeEvery(updateEcommerceOrder.type, updateEcommerceOrderSaga);
  yield takeEvery(cancelEcommerceOrder.type, cancelEcommerceOrderSaga);
  yield takeEvery(refundEcommerceOrder.type, refundEcommerceOrderSaga);
  yield takeEvery(validateCoupon.type, validateCouponSaga);
  yield takeEvery(generateShareLink.type, generateShareLinkSaga);
  yield takeLatest(
    getAvailablePaymentMethods.type,
    getAvailablePaymentMethodsSaga
  );
  yield takeLatest(attachPaymentMethod.type, attachPaymentMethodSaga);
}

export default EcommerceOrders;
